import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  preparePerthImportWizard,
  applyPerthImportWizardBody,
  applyPerthImportWizardPatternStage,
} from '@/lib/api';

const DEFAULT_MASTER_FOLDER_ID = '0B6OuuQYfjH_HTFVwdGFJcFNneXc';

const scoreToColor = (score) => {
  const value = Number(score || 0);
  if (value >= 85) return 'bg-emerald-100 text-emerald-700';
  if (value >= 60) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

const normalizeVersionToken = (value) => String(value ?? '').trim().replace(/\.0+$/, '');

export default function PerthImportWizardTab({ customerId, customer, onMeasurementsUpdated, onPatternsUpdated, measurements = [] }) {
  const [masterFolderId, setMasterFolderId] = useState(DEFAULT_MASTER_FOLDER_ID);
  const [prepareLoading, setPrepareLoading] = useState(false);
  const [prepareResult, setPrepareResult] = useState(null);

  const [bodyDraftVersions, setBodyDraftVersions] = useState([]);
  const [saveBodyLoading, setSaveBodyLoading] = useState(false);
  const [savedBodyVersionMap, setSavedBodyVersionMap] = useState({});

  const [stageDrafts, setStageDrafts] = useState({});
  const [savingStageId, setSavingStageId] = useState('');
  const [savedStages, setSavedStages] = useState({});
  const [stageMeasurementOverrides, setStageMeasurementOverrides] = useState({});

  const measurementOptions = useMemo(() => {
    return [...(measurements || [])]
      .sort((a, b) => new Date(b.measurement_date || 0) - new Date(a.measurement_date || 0))
      .map((measurement) => ({
        measurement_id: measurement.measurement_id,
        label: `${measurement.measurement_date || 'No date'} • ${measurement.measurement_id?.slice(0, 8) || ''}`,
      }));
  }, [measurements]);

  const handlePrepare = async () => {
    try {
      setPrepareLoading(true);
      const res = await preparePerthImportWizard({
        customer_id: customerId,
        master_folder_id: masterFolderId.trim(),
      });
      const data = res.data;
      setPrepareResult(data);

      const bodyDraft = (data?.body_versions || []).map((version) => ({
        version_token: normalizeVersionToken(version.version_token),
        measurement_date: version.measurement_date || '',
        skip: Boolean(version.already_imported || version.skip_recommended),
        already_imported: Boolean(version.already_imported),
        existing_measurement_id: version.existing_measurement_id || '',
        existing_measurement_date: version.existing_measurement_date || '',
        rows: (version.rows || []).map((row) => ({
          field_key: row.field_key,
          field_label: row.field_label,
          value: row.editable_value ?? '',
          source: row.source || '',
          note: row.note || '',
        })),
      }));
      setBodyDraftVersions(bodyDraft);

      const existingImportedMap = {};
      bodyDraft.forEach((version) => {
        if (version.existing_measurement_id) {
          existingImportedMap[normalizeVersionToken(version.version_token)] = version.existing_measurement_id;
        }
      });

      const stages = {};
      (data?.pattern_stages || []).forEach((stage) => {
        stages[stage.stage_id] = {
          ...stage,
          rows: (stage.rows || []).map((row) => ({
            ...row,
            pattern_value: row.pattern_value ?? '',
          })),
        };
      });
      setStageDrafts(stages);
      setSavedStages({});
      setSavedBodyVersionMap(existingImportedMap);
      setStageMeasurementOverrides({});

      toast.success('Wizard data prepared. Review body step first.');
    } catch (error) {
      console.error('Wizard prepare failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to prepare import wizard');
    } finally {
      setPrepareLoading(false);
    }
  };

  const updateBodyCell = (versionToken, fieldKey, value) => {
    setBodyDraftVersions((prev) =>
      prev.map((version) => {
        if (version.version_token !== versionToken) return version;
        return {
          ...version,
          rows: version.rows.map((row) =>
            row.field_key === fieldKey
              ? { ...row, value }
              : row
          ),
        };
      })
    );
  };

  const updateBodyVersionMeta = (versionToken, patch) => {
    setBodyDraftVersions((prev) =>
      prev.map((version) => (version.version_token === versionToken ? { ...version, ...patch } : version))
    );
  };

  const handleSaveBody = async () => {
    try {
      setSaveBodyLoading(true);
      const payload = {
        customer_id: customerId,
        versions: bodyDraftVersions.map((version) => ({
          version_token: normalizeVersionToken(version.version_token),
          measurement_date: version.measurement_date || null,
          skip: Boolean(version.skip),
          rows: version.rows.map((row) => ({
            field_key: row.field_key,
            value: String(row.value ?? ''),
            source: row.source,
            note: row.note || null,
          })),
        })),
      };
      const res = await applyPerthImportWizardBody(payload);
      const rows = res.data?.results || [];
      const nextMap = {};
      rows.forEach((row) => {
        if (!row?.skipped && row?.measurement_id) {
          nextMap[normalizeVersionToken(row.version_token)] = row.measurement_id;
        }
      });
      setSavedBodyVersionMap((prev) => ({
        ...prev,
        ...nextMap,
      }));
      onMeasurementsUpdated?.();
      toast.success(`Body step saved. Saved: ${res.data?.saved || 0}, Skipped: ${res.data?.skipped || 0}`);
    } catch (error) {
      console.error('Save body failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to save body measurements');
    } finally {
      setSaveBodyLoading(false);
    }
  };

  const updatePatternRow = (stageId, specKey, value) => {
    setStageDrafts((prev) => ({
      ...prev,
      [stageId]: {
        ...prev[stageId],
        rows: (prev[stageId]?.rows || []).map((row) =>
          row.spec_key === specKey
            ? { ...row, pattern_value: value }
            : row
        ),
      },
    }));
  };

  const handleSaveStage = async (stageId) => {
    const stage = stageDrafts[stageId];
    if (!stage) return;

    try {
      setSavingStageId(stageId);
      const normalizedStageToken = normalizeVersionToken(stage.version_token);
      const linkedBodyVersion = (bodyDraftVersions || []).find(
        (version) => normalizeVersionToken(version.version_token) === normalizedStageToken
      );
      const manualOverrideMeasurementId = stageMeasurementOverrides[stageId] || null;
      const payload = {
        customer_id: customerId,
        garment_type: stage.garment_type,
        tab_name: stage.tab_name,
        version_token: normalizedStageToken,
        measurement_date: linkedBodyVersion?.measurement_date || null,
        pattern_code: stage.pattern_code,
        measurement_id: manualOverrideMeasurementId || savedBodyVersionMap[normalizedStageToken] || linkedBodyVersion?.existing_measurement_id || null,
        metadata: stage.metadata || {},
        rows: (stage.rows || []).map((row) => ({
          spec_key: row.spec_key,
          part_label: row.part_label,
          pattern_value: String(row.pattern_value ?? ''),
          confidence_score: row.confidence_score,
          confidence_reason: row.confidence_reason,
        })),
        auto_create_spec_fields: false,
      };

      await applyPerthImportWizardPatternStage(payload);
      setSavedStages((prev) => ({ ...prev, [stageId]: true }));
      onPatternsUpdated?.();
      toast.success(`Saved stage ${stage.garment_type} tab ${stage.tab_name}`);
    } catch (error) {
      console.error('Save stage failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to save pattern stage');
    } finally {
      setSavingStageId('');
    }
  };

  const stageList = useMemo(() => (prepareResult?.pattern_stages || []), [prepareResult]);

  return (
    <div className="space-y-6" data-testid="perth-import-wizard-root">
      <Card data-testid="perth-import-wizard-config-card">
        <CardHeader>
          <CardTitle data-testid="perth-import-wizard-title">Perth Import Wizard</CardTitle>
          <CardDescription data-testid="perth-import-wizard-description">
            Step 1: Body measurements review. Step 2: Pattern stages (Jacket → Shirt → Waistcoat → Trouser).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label data-testid="perth-import-wizard-customer-label">Customer</Label>
              <Input
                value={`${customer?.customer_name || ''} (${customer?.customer_no || customer?.customer_number || '-'})`}
                disabled
                data-testid="perth-import-wizard-customer-display"
              />
            </div>
            <div className="space-y-2">
              <Label data-testid="perth-import-wizard-master-folder-label">Master Folder ID</Label>
              <Input
                value={masterFolderId}
                onChange={(e) => setMasterFolderId(e.target.value)}
                data-testid="perth-import-wizard-master-folder-input"
              />
            </div>
          </div>
          <Button onClick={handlePrepare} disabled={prepareLoading} data-testid="perth-import-wizard-prepare-button">
            {prepareLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Prepare Wizard Data
          </Button>
        </CardContent>
      </Card>

      {prepareResult && (
        <Card data-testid="perth-import-wizard-summary-card">
          <CardContent className="pt-6 flex flex-wrap gap-2">
            <Badge variant="secondary" data-testid="perth-import-wizard-summary-db">DB: {prepareResult?.target_db_name || '-'}</Badge>
            <Badge variant="secondary" data-testid="perth-import-wizard-summary-body-versions">Body versions: {prepareResult?.summary?.body_versions || 0}</Badge>
            <Badge variant="secondary" data-testid="perth-import-wizard-summary-pattern-stages">Pattern stages: {prepareResult?.summary?.pattern_stages || 0}</Badge>
          </CardContent>
        </Card>
      )}

      {prepareResult && (
        <Card data-testid="perth-import-wizard-body-step-card">
          <CardHeader>
            <CardTitle data-testid="perth-import-wizard-body-step-title">Step 1: Body Measurements</CardTitle>
            <CardDescription data-testid="perth-import-wizard-body-step-description">
              Column 1 = app field, Column 2 = derived/editable value, Column 3 = source sheet.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(bodyDraftVersions || []).map((version) => (
              <details key={version.version_token} className="rounded border bg-white" open data-testid={`perth-import-wizard-body-version-${version.version_token}`}>
                <summary className="px-3 py-2 cursor-pointer flex items-center justify-between">
                  <span className="font-medium">Version / Tab {version.version_token}</span>
                  <div className="flex items-center gap-2">
                    {version.already_imported ? (
                      <Badge className="bg-amber-100 text-amber-700" data-testid={`perth-import-wizard-body-version-imported-${version.version_token}`}>
                        Imported already{version.existing_measurement_date ? ` (${version.existing_measurement_date})` : ''}
                      </Badge>
                    ) : null}
                    {savedBodyVersionMap[normalizeVersionToken(version.version_token)] ? (
                      <Badge className="bg-emerald-100 text-emerald-700" data-testid={`perth-import-wizard-body-version-saved-${version.version_token}`}>Saved</Badge>
                    ) : null}
                  </div>
                </summary>
                <div className="px-3 pb-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <Label className="text-xs">Measurement date</Label>
                      <Input
                        value={version.measurement_date || ''}
                        onChange={(e) => updateBodyVersionMeta(version.version_token, { measurement_date: e.target.value })}
                        data-testid={`perth-import-wizard-body-date-${version.version_token}`}
                      />
                    </div>
                    <label className="inline-flex items-center gap-2 mt-6" data-testid={`perth-import-wizard-body-skip-wrap-${version.version_token}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(version.skip)}
                        onChange={(e) => updateBodyVersionMeta(version.version_token, { skip: e.target.checked })}
                        data-testid={`perth-import-wizard-body-skip-${version.version_token}`}
                      />
                      Skip this version (no change)
                    </label>
                  </div>

                  <div className="rounded border overflow-hidden" data-testid={`perth-import-wizard-body-table-${version.version_token}`}>
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-2 py-2">App Required Body Measurement</th>
                          <th className="text-left px-2 py-2">Derived / Editable Value</th>
                          <th className="text-left px-2 py-2">Derived From</th>
                        </tr>
                      </thead>
                      <tbody>
                        {version.rows.map((row) => (
                          <tr key={row.field_key} className="border-t" data-testid={`perth-import-wizard-body-row-${version.version_token}-${row.field_key}`}>
                            <td className="px-2 py-1.5">{row.field_label}</td>
                            <td className="px-2 py-1.5">
                              <Input
                                value={row.value ?? ''}
                                onChange={(e) => updateBodyCell(version.version_token, row.field_key, e.target.value)}
                                data-testid={`perth-import-wizard-body-value-${version.version_token}-${row.field_key}`}
                              />
                            </td>
                            <td className="px-2 py-1.5 text-slate-600">{row.source || '-'}{row.note ? ` • ${row.note}` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            ))}

            <Button onClick={handleSaveBody} disabled={saveBodyLoading || bodyDraftVersions.length === 0} data-testid="perth-import-wizard-save-body-button">
              {saveBodyLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Body Measurements
            </Button>
          </CardContent>
        </Card>
      )}

      {prepareResult && (
        <Card className="w-full" data-testid="perth-import-wizard-pattern-step-card">
          <CardHeader>
            <CardTitle data-testid="perth-import-wizard-pattern-step-title">Step 2: Pattern Stages</CardTitle>
            <CardDescription data-testid="perth-import-wizard-pattern-step-description">
              Save each stage independently in sequence: Jacket → Shirt → Waistcoat → Trouser.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4" data-testid="perth-import-wizard-pattern-stages-container">
                {stageList.map((stage) => {
                  const draft = stageDrafts[stage.stage_id] || stage;
                  const isSaved = Boolean(savedStages[stage.stage_id]);
                  return (
                    <details key={stage.stage_id} className="rounded border bg-white" open data-testid={`perth-import-wizard-stage-${stage.stage_id}`}>
                      <summary className="px-3 py-2 cursor-pointer flex items-center justify-between">
                        <span className="font-medium">
                          {stage.garment_type.toUpperCase()} • Tab {stage.tab_name} • {stage.pattern_code}
                        </span>
                        {isSaved ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        )}
                      </summary>
                      <div className="px-3 pb-3 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                          <div className="space-y-1">
                            <Label className="text-xs" data-testid={`perth-import-wizard-stage-linked-measurement-label-${stage.stage_id}`}>
                              Linked Body Measurement (optional override)
                            </Label>
                            <select
                              className="w-full h-9 rounded border px-2 text-sm"
                              value={stageMeasurementOverrides[stage.stage_id] || ''}
                              onChange={(e) => setStageMeasurementOverrides((prev) => ({ ...prev, [stage.stage_id]: e.target.value || '' }))}
                              data-testid={`perth-import-wizard-stage-linked-measurement-select-${stage.stage_id}`}
                            >
                              <option value="">Auto-link by version/date/latest</option>
                              {measurementOptions.map((option) => (
                                <option key={option.measurement_id} value={option.measurement_id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="rounded border overflow-x-auto" data-testid={`perth-import-wizard-stage-table-${stage.stage_id}`}>
                          <table className="w-full min-w-[1100px] text-sm" data-testid={`perth-import-wizard-stage-table-element-${stage.stage_id}`}>
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="text-left px-2 py-2 w-[220px]">Specification Key</th>
                                <th className="text-left px-2 py-2 w-[260px]">Specification Label</th>
                                <th className="text-left px-2 py-2 w-[180px]">Body Measurement</th>
                                <th className="text-left px-2 py-2 min-w-[240px]">Pattern Measurement (Editable)</th>
                                <th className="text-left px-2 py-2 min-w-[300px]">Confidence</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(draft.rows || []).map((row) => (
                                <tr key={row.spec_key} className="border-t" data-testid={`perth-import-wizard-stage-row-${stage.stage_id}-${row.spec_key}`}>
                                  <td className="px-2 py-1.5 font-mono text-xs text-slate-700" data-testid={`perth-import-wizard-stage-spec-key-${stage.stage_id}-${row.spec_key}`}>
                                    {row.spec_key}
                                  </td>
                                  <td className="px-2 py-1.5 font-medium" data-testid={`perth-import-wizard-stage-spec-label-${stage.stage_id}-${row.spec_key}`}>
                                    {row.part_label || row.spec_key}
                                  </td>
                                  <td className="px-2 py-1.5">{row.body_value ?? '-'}</td>
                                  <td className="px-2 py-1.5">
                                    <Input
                                      value={row.pattern_value ?? ''}
                                      onChange={(e) => updatePatternRow(stage.stage_id, row.spec_key, e.target.value)}
                                      data-testid={`perth-import-wizard-stage-pattern-value-${stage.stage_id}-${row.spec_key}`}
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold ${scoreToColor(row.confidence_score)}`}>
                                      {row.confidence_score}/100 • {row.confidence_label}
                                    </span>
                                    <div className="text-[11px] text-slate-600 mt-1">{row.confidence_reason}</div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <Button
                          onClick={() => handleSaveStage(stage.stage_id)}
                          disabled={savingStageId === stage.stage_id}
                          data-testid={`perth-import-wizard-save-stage-${stage.stage_id}`}
                        >
                          {savingStageId === stage.stage_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Save This Stage
                        </Button>
                      </div>
                    </details>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
